import React, { useState } from "react";
import { X, Pencil, Trash2 } from "lucide-react";
import Tag from "../../ui/Tag/Tag.jsx";
import Pagination from "../../ui/Pagination/Pagination.jsx";
import TriStateCheckbox from "../../ui/TriStateCheckbox/TriStateCheckbox.jsx";
import { WEIGHTS } from "../../../data/rates.js";
import { truckTax } from "../../../utils/tax.js";
import { money } from "../../../utils/format.js";

const PAGE_SIZE = 10;

// weightCategories: backend lookup rows ({ code, weightLabel }), fetched by
// TrucksStep - falls back to the static WEIGHTS map when empty (dashboard
// demo path, or before the lookup has loaded).
const weightLabelFor = (cat, weightCategories) =>
  weightCategories.find((w) => w.code === cat)?.weightLabel ?? WEIGHTS[cat];

// A row's selection key: its real backend id when one exists, else its
// position in the full (unpaginated) trucks array - matches the fallback
// TrucksStep already uses for onRemove/onEdit indices, and stays stable
// across pages since it's derived from the full array, not the page slice.
const rowKey = (t, i) => t.id ?? i;

const TrucksTable = ({
  trucks,
  month,
  onRemove,
  onEdit,
  weightCategories = [],
  showCategoryLabel = true,
  // Bulk-select is opt-in: omitting selectedIds (the default) renders the
  // table exactly as before - no checkbox column - so ReviewStep's
  // read-only usage is untouched.
  selectedIds,
  onToggleRow,
  onToggleAll,
}) => {
  const [page, setPage] = useState(0);
  const showActions = !!(onEdit || onRemove);
  const selectable = !!selectedIds;

  // "Select all" always means every loaded row, not just the current page -
  // this table already loads the whole list client-side (see Requirement 15),
  // so there's no server-side page the user could be surprised by.
  const allIds = selectable ? trucks.map(rowKey) : [];
  const selectedCount = selectable ? allIds.reduce((n, id) => n + (selectedIds.has(id) ? 1 : 0), 0) : 0;
  const headerChecked = selectable && allIds.length > 0 && selectedCount === allIds.length;
  const headerIndeterminate = selectable && selectedCount > 0 && selectedCount < allIds.length;

  if (!trucks.length) {
    return (
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <TableHead showActions={showActions} selectable={selectable} />
          <tbody>
            <tr>
              <td colSpan={(showActions ? 6 : 5) + (selectable ? 1 : 0)} className="text-[var(--muted)] p-[22px_12px]">
                No trucks yet. Add your first one below.
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  }

  // onRemove/onEdit index into the full trucks array by position (see
  // TrucksStep.startEdit/removeTruck), so rows must keep their real index
  // even once only a page-sized slice of them is rendered.
  const pageCount = Math.max(1, Math.ceil(trucks.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const start = safePage * PAGE_SIZE;
  const pageRows = trucks.slice(start, start + PAGE_SIZE);

  return (
    <>
      <div className="overflow-x-auto">
      <table className="w-full border-collapse">
        <TableHead
          showActions={showActions}
          selectable={selectable}
          headerChecked={headerChecked}
          headerIndeterminate={headerIndeterminate}
          onToggleAll={() => onToggleAll?.(!headerChecked ? allIds : [])}
        />
        <tbody>
          {pageRows.map((t, pageIndex) => {
            const i = start + pageIndex;
            const key = rowKey(t, i);
            return (
              <tr key={t.id ?? t.vin + i} className="hover:bg-[var(--accent-soft)] transition-colors">
                {selectable && (
                  <td className="text-left p-3 border-b border-[var(--line)]">
                    <TriStateCheckbox
                      checked={selectedIds.has(key)}
                      onChange={() => onToggleRow?.(key)}
                      label={`Select row ${i + 1}`}
                    />
                  </td>
                )}
                <td className="text-left p-3 border-b border-[var(--line)]">{i + 1}</td>
                <td className="text-left p-3 border-b border-[var(--line)] font-mono text-[13px]">{t.vin || "—"}</td>
                <td className="text-left p-3 border-b border-[var(--line)]">
                  {t.suspended ? "W" : t.cat}
                  {showCategoryLabel && <> · {t.suspended ? "suspended" : weightLabelFor(t.cat, weightCategories)}</>}
                </td>
                <td className="text-left p-3 border-b border-[var(--line)]">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {t.suspended && <Tag tone="susp">Suspended</Tag>}
                    {t.logging && <Tag tone="log">Logging</Tag>}
                    {!t.suspended && !t.logging && <Tag>Standard</Tag>}
                  </div>
                  {t.suspended && (
                    <small className="block text-[var(--muted)] text-[12px] mt-1">
                      {t.agri ? "Agricultural · 7,500 mi" : "5,000 mi"}
                    </small>
                  )}
                </td>
                <td className="text-right p-3 border-b border-[var(--line)] font-mono text-[13px]">
                  {money(t.taxAmt != null ? Number(t.taxAmt) : truckTax(t, month))}
                  {!showActions && onRemove && (
                    <button
                      title="Remove"
                      onClick={() => onRemove(i)}
                      className="border-0 bg-transparent text-[var(--dim)] cursor-pointer text-base ml-2 hover:text-[var(--danger)]"
                    >
                      <X size={14} className="inline" />
                    </button>
                  )}
                </td>
                {showActions && (
                  <td className="text-right p-3 border-b border-[var(--line)] whitespace-nowrap">
                    {onEdit && (
                      <button
                        title="Edit"
                        onClick={() => onEdit(i)}
                        className="inline-flex items-center justify-center border-0 bg-transparent text-[var(--dim)] cursor-pointer p-1 hover:text-[var(--accent)]"
                      >
                        <Pencil size={14} />
                      </button>
                    )}
                    {onRemove && (
                      <button
                        title="Delete"
                        onClick={() => onRemove(i)}
                        className="inline-flex items-center justify-center border-0 bg-transparent text-[var(--dim)] cursor-pointer p-1 ml-1 hover:text-[var(--danger)]"
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
      </div>
      <Pagination page={safePage} pageCount={pageCount} onChange={setPage} />
    </>
  );
};

const TableHead = ({ showActions, selectable = false, headerChecked = false, headerIndeterminate = false, onToggleAll }) => {
  const headers = ["#", "VIN", "Category", "Type", "Tax", ...(showActions ? ["Actions"] : [])];
  // "Tax" (and "Actions", when present) right-align - these are the last two
  // header labels regardless of how many columns precede them, so the
  // right-align check counts from the end instead of a fixed index that
  // would otherwise need adjusting whenever a column (like Select) is added.
  const rightAlignFrom = headers.length - (showActions ? 2 : 1);
  return (
    <thead>
      <tr>
        {selectable && (
          <th className="text-left p-3 border-b border-[var(--line)] w-[36px]">
            <TriStateCheckbox
              checked={headerChecked}
              indeterminate={headerIndeterminate}
              onChange={onToggleAll}
              label="Select all trucks"
            />
          </th>
        )}
        {headers.map((h, i) => (
          <th
            key={h + i}
            className={[
              "text-left p-3 border-b border-[var(--line)] font-disp uppercase tracking-[.07em] text-[11px] text-[var(--muted)] font-semibold",
              i >= rightAlignFrom ? "text-right" : "",
            ].join(" ")}
          >
            {h}
          </th>
        ))}
      </tr>
    </thead>
  );
};

export default TrucksTable;
